import { Component } from '@theme/component';
import { prefersReducedMotion, debounce } from '@theme/utilities';

/**
 * A draggable, optionally auto-scrolling image carousel laid out as a repeating
 * pyramid (single / pair / big / pair ...).
 *
 * Movement is transform-based rather than native scroll so that autoplay glides
 * continuously and the loop is seamless in both directions. The original column
 * sequence is cloned enough times to fill twice the viewport; the translation
 * offset is wrapped by one "unit" (a whole number of sequence repeats wider than
 * the viewport), which keeps clones and originals perfectly aligned so the wrap
 * is invisible.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The flex row of columns that gets translated.
 *
 * @extends Component<Refs>
 */
class PyramidCarousel extends Component {
  requiredRefs = ['track'];

  /** Friction applied to fling momentum each frame (per 16ms). */
  static FRICTION = 0.94;
  /** Below this speed (px/ms) momentum is considered finished. */
  static MIN_VELOCITY = 0.01;
  /** Hard ceiling on clones so a single-image carousel can't explode the DOM. */
  static MAX_COPIES_PER_UNIT = 40;

  /** @type {string} Markup of the original (un-cloned) column sequence. */
  #sourceHTML = '';
  /** @type {number} Number of columns in one original sequence. */
  #colsPerSeq = 0;
  /** @type {number} Pixel distance between equivalent columns one sequence apart. */
  #period = 0;
  /** @type {number} Width of one wrap unit (>= viewport, a multiple of #period). */
  #unitWidth = 0;

  /** @type {number} Current translation offset in px, kept within [0, #unitWidth). */
  #offset = 0;
  /** @type {number | null} requestAnimationFrame handle. */
  #raf = null;
  /** @type {number} Timestamp of the previous animation frame. */
  #lastTime = 0;
  /** @type {number} Fling velocity in px/ms (sign follows #offset). */
  #velocity = 0;

  /** @type {boolean} Whether autoplay is enabled (respects reduced motion). */
  #autoplay = false;
  /** @type {number} Autoplay speed in px/ms. */
  #speed = 0;
  /** @type {boolean} Paused by hover, drag, or the theme editor. */
  #paused = false;

  /** @type {number | null} Active drag pointer id. */
  #pointerId = null;
  /** @type {boolean} */
  #dragging = false;
  /** @type {number} Pointer X at the previous move, for delta + velocity. */
  #lastPointerX = 0;
  /** @type {number} Total horizontal travel during the current gesture. */
  #dragDistance = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#sourceHTML = this.refs.track.innerHTML;
    this.#autoplay = this.dataset.autoplay === 'true' && !prefersReducedMotion();
    this.#speed = Number(this.dataset.autoplaySpeed || 0);

    this.#build({ center: true });

    this.refs.track.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    window.addEventListener('resize', this.#onResize);

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', this.#onBlockSelect);
      this.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    }

    if (this.#autoplay) this.#requestTick();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopTick();
    this.refs.track?.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
    window.removeEventListener('pointercancel', this.#onPointerUp);
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
  }

  /**
   * (Re)build the cloned track for the current viewport width.
   * @param {{ center?: boolean }} [options]
   */
  #build({ center = false } = {}) {
    const { track } = this.refs;

    track.style.transform = 'none';
    track.innerHTML = this.#sourceHTML;

    const source = /** @type {HTMLElement[]} */ (Array.from(track.children));
    this.#colsPerSeq = source.length;
    if (this.#colsPerSeq === 0) return;

    // One clone copy lets us measure the true repeat period (column + gap).
    this.#appendCopy(source);
    this.#period = track.children[this.#colsPerSeq].offsetLeft - track.children[0].offsetLeft;
    if (this.#period <= 0) return;

    const viewport = this.clientWidth || window.innerWidth;
    const copiesPerUnit = Math.min(
      PyramidCarousel.MAX_COPIES_PER_UNIT,
      Math.max(1, Math.ceil(viewport / this.#period))
    );
    this.#unitWidth = copiesPerUnit * this.#period;

    // Two units of content guarantee the visible window is always filled.
    for (let copy = 2; copy < copiesPerUnit * 2; copy++) this.#appendCopy(source);

    if (center) this.#offset = this.#centeredOffset();
    this.#offset = this.#wrap(this.#offset);
    this.#apply();
  }

  /**
   * Append an aria-hidden clone of the original column sequence.
   * @param {HTMLElement[]} source
   */
  #appendCopy(source) {
    const fragment = document.createDocumentFragment();
    for (const column of source) {
      const clone = /** @type {HTMLElement} */ (column.cloneNode(true));
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('ref');
      fragment.appendChild(clone);
    }
    this.refs.track.appendChild(fragment);
  }

  /** Offset that places the first "big" column at the centre of the viewport. */
  #centeredOffset() {
    const big = /** @type {HTMLElement | null} */ (this.refs.track.querySelector('.pyramid__col--big'));
    if (!big) return 0;
    const viewport = this.clientWidth || window.innerWidth;
    return big.offsetLeft + big.offsetWidth / 2 - viewport / 2;
  }

  /**
   * Wrap an offset into [0, #unitWidth) so clones and originals stay aligned.
   * @param {number} value
   */
  #wrap(value) {
    if (this.#unitWidth <= 0) return 0;
    return ((value % this.#unitWidth) + this.#unitWidth) % this.#unitWidth;
  }

  #apply() {
    this.refs.track.style.transform = `translate3d(${-this.#offset}px, 0, 0)`;
  }

  #requestTick() {
    if (this.#raf != null) return;
    this.#lastTime = performance.now();
    this.#raf = requestAnimationFrame(this.#tick);
  }

  #stopTick() {
    if (this.#raf != null) cancelAnimationFrame(this.#raf);
    this.#raf = null;
  }

  /** @param {number} now */
  #tick = (now) => {
    const dt = Math.min(now - this.#lastTime, 64); // clamp after tab-switch stalls
    this.#lastTime = now;

    let moving = false;

    if (!this.#dragging) {
      if (Math.abs(this.#velocity) > PyramidCarousel.MIN_VELOCITY) {
        this.#offset += this.#velocity * dt;
        this.#velocity *= Math.pow(PyramidCarousel.FRICTION, dt / 16);
        moving = true;
      } else {
        this.#velocity = 0;
      }

      if (this.#autoplay && !this.#paused) {
        this.#offset += this.#speed * dt;
        moving = true;
      }
    }

    this.#offset = this.#wrap(this.#offset);
    this.#apply();

    // Keep ticking while there's anything to animate; otherwise idle.
    if (moving || this.#dragging || (this.#autoplay && !this.#paused)) {
      this.#raf = requestAnimationFrame(this.#tick);
    } else {
      this.#raf = null;
    }
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.button != null && event.button !== 0) return;

    this.#pointerId = event.pointerId;
    this.#dragging = true;
    this.#velocity = 0;
    this.#lastPointerX = event.clientX;
    this.#dragDistance = 0;

    window.addEventListener('pointermove', this.#onPointerMove);
    window.addEventListener('pointerup', this.#onPointerUp);
    // Touch gestures that turn into a vertical page scroll fire `pointercancel`
    // (not `pointerup`); without this the drag never ends and autoplay stalls.
    window.addEventListener('pointercancel', this.#onPointerUp);
    this.#requestTick();
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (event.pointerId !== this.#pointerId) return;

    const delta = event.clientX - this.#lastPointerX;
    this.#lastPointerX = event.clientX;
    this.#dragDistance += Math.abs(delta);

    // Dragging right reveals earlier content, so the offset decreases.
    this.#offset -= delta;
    if (event.movementX !== 0 || delta !== 0) this.#velocity = -delta / 16;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (event.pointerId !== this.#pointerId) return;

    this.#dragging = false;
    this.#pointerId = null;
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
    window.removeEventListener('pointercancel', this.#onPointerUp);

    // Suppress the click that follows a real drag so images don't feel "sticky".
    if (this.#dragDistance > 8) {
      const suppress = (/** @type {Event} */ e) => {
        e.stopPropagation();
        e.preventDefault();
      };
      this.refs.track.addEventListener('click', suppress, { capture: true, once: true });
      window.setTimeout(() => this.refs.track.removeEventListener('click', suppress, { capture: true }), 0);
    }

    this.#requestTick();
  };

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    // Hover-pause is for mouse only; touch "hover" never reliably releases.
    if (event.pointerType !== 'mouse') return;
    this.#paused = true;
  };

  /** @param {PointerEvent} event */
  #onPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#paused = false;
    if (this.#autoplay) this.#requestTick();
  };

  #onResize = debounce(() => this.#build(), 200);

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    this.#paused = true;
    const target = /** @type {HTMLElement} */ (/** @type {CustomEvent} */ (event).detail?.blockId);
    const selected = this.refs.track.querySelector(`[data-block-id="${target}"]`);
    if (!(selected instanceof HTMLElement)) return;

    const viewport = this.clientWidth || window.innerWidth;
    this.#offset = this.#wrap(selected.offsetLeft + selected.offsetWidth / 2 - viewport / 2);
    this.#apply();
  };

  #onBlockDeselect = () => {
    this.#paused = false;
    if (this.#autoplay) this.#requestTick();
  };
}

if (!customElements.get('pyramid-carousel')) {
  customElements.define('pyramid-carousel', PyramidCarousel);
}
